/**
 * searchThreadCorpus — the ONE search rule over what the browser holds
 * (chat-thread experience PR-5, 2026-09-09): thread names (current AND
 * former — a thread found by the name it used to have still opens) and the
 * text of messages the browser has. Pure: the search box feeds it the
 * corpus; the deep tier (functions/src/prest0nSession/threadSearch.ts) scores
 * the server's copy with the same scoring (threadSearchCore, parity-pinned).
 *
 * Title matching is a folded substring match (case- and accent-insensitive):
 * exact > prefix > word start > anywhere; a former name scores a step lower
 * than the current one. Message matching reuses the cross-thread search's
 * tokenizer + idf-ish scorer + snippet window, so Prest0n's own recall tool
 * and the user's box agree on what "matches".
 */
import { extractSnippet, scoreDocument, tokenize } from '../crossThreadSearch';

export interface SearchableThread {
  id: string;
  /** The name the list shows (the display-title rule's output). */
  title: string;
  /** Former names (rename history), any order. */
  formerTitles?: readonly string[];
  archived?: boolean;
  /** ISO — recency tie-break. */
  lastActiveAt?: string;
}

export interface SearchableMessage {
  threadId: string;
  id: string;
  /** The shared-store id the row can be scrolled to (see scrollToSharedMessage). */
  sharedMessageId?: string | null;
  text: string;
  isUser?: boolean;
  /** ISO. */
  timestamp?: string;
  senderId?: string;
}

export type ThreadSearchHitKind = 'thread' | 'former-title' | 'message';

export interface ThreadSearchHit {
  kind: ThreadSearchHitKind;
  threadId: string;
  /** The thread's CURRENT display title. */
  title: string;
  /** What matched — the former name, or the message window. */
  snippet: string;
  score: number;
  messageId?: string;
  sharedMessageId?: string;
  timestamp?: string;
  isUser?: boolean;
}

export interface ThreadSearchCorpus {
  threads: readonly SearchableThread[];
  messages?: readonly SearchableMessage[];
}

export interface SearchThreadCorpusOptions {
  maxThreadHits?: number;
  maxMessageHits?: number;
  maxSnippetChars?: number;
}

export interface ThreadSearchResults {
  threads: ThreadSearchHit[];
  messages: ThreadSearchHit[];
}

const STOPWORDS = new Set([
  'the', 'and', 'for', 'that', 'this', 'with', 'was', 'were', 'are', 'have', 'has', 'had', 'you', 'your',
  'que', 'con', 'los', 'las', 'del', 'para', 'por', 'como', 'una', 'uno',
]);

/** Lowercase, accent-folded, single-spaced. */
export function foldSearchText(value: unknown): string {
  return String(value ?? '')
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .replace(/\s+/g, ' ')
    .trim();
}

/** 0 = no match; 100 exact, 80 prefix, 60 a word starts with it, 40 anywhere. */
export function titleMatchScore(title: unknown, foldedQuery: string): number {
  const folded = foldSearchText(title);
  if (!folded || !foldedQuery) return 0;
  if (folded === foldedQuery) return 100;
  if (folded.startsWith(foldedQuery)) return 80;
  const idx = folded.indexOf(foldedQuery);
  if (idx === -1) return 0;
  return folded[idx - 1] === ' ' ? 60 : 40;
}

const isStopword = (t: string): boolean => STOPWORDS.has(t);

export function searchThreadCorpus(
  rawQuery: string,
  corpus: ThreadSearchCorpus,
  options: SearchThreadCorpusOptions = {},
): ThreadSearchResults {
  const foldedQuery = foldSearchText(rawQuery);
  const maxThreadHits = options.maxThreadHits ?? 12;
  const maxMessageHits = options.maxMessageHits ?? 12;
  const maxSnippetChars = options.maxSnippetChars ?? 160;
  if (!foldedQuery) return { threads: [], messages: [] };

  // ── Titles: current name first; a former name a step lower ────────────
  const threadHits: ThreadSearchHit[] = [];
  for (const t of corpus.threads ?? []) {
    if (!t || !t.id) continue;
    const current = titleMatchScore(t.title, foldedQuery);
    let best: ThreadSearchHit | null = current > 0
      ? { kind: 'thread', threadId: t.id, title: t.title, snippet: t.title, score: current, timestamp: t.lastActiveAt }
      : null;
    for (const former of t.formerTitles ?? []) {
      const s = titleMatchScore(former, foldedQuery) * 0.8;
      if (s > 0 && (!best || s > best.score)) {
        best = { kind: 'former-title', threadId: t.id, title: t.title, snippet: former, score: s, timestamp: t.lastActiveAt };
      }
    }
    if (best) threadHits.push(best);
  }
  threadHits.sort((a, b) => b.score - a.score || (b.timestamp || '').localeCompare(a.timestamp || ''));

  // ── Messages: the cross-thread scorer over the browser-held text ──────
  const messageHits: ThreadSearchHit[] = [];
  const messages = corpus.messages ?? [];
  if (messages.length > 0) {
    const queryTokens = tokenize(rawQuery).filter((t) => !isStopword(t));
    const titleById = new Map<string, string>();
    for (const t of corpus.threads ?? []) if (t?.id) titleById.set(t.id, t.title);
    if (queryTokens.length > 0) {
      const docs: Array<{ m: SearchableMessage; tokens: string[] }> = [];
      const df = new Map<string, number>();
      for (const m of messages) {
        const text = typeof m?.text === 'string' ? m.text : '';
        if (!m?.threadId || text.trim().length < 2) continue;
        const tokens = tokenize(text).filter((t) => !isStopword(t));
        if (tokens.length === 0) continue;
        for (const tok of new Set(tokens)) df.set(tok, (df.get(tok) || 0) + 1);
        docs.push({ m, tokens });
      }
      for (const { m, tokens } of docs) {
        let score = scoreDocument(queryTokens, tokens, df);
        // A verbatim phrase in the text is a stronger signal than token overlap.
        if (foldSearchText(m.text).includes(foldedQuery)) score += 1;
        if (score <= 0) continue;
        messageHits.push({
          kind: 'message',
          threadId: m.threadId,
          title: titleById.get(m.threadId) ?? '',
          snippet: extractSnippet(m.text, queryTokens, maxSnippetChars),
          score,
          messageId: m.id,
          ...(m.sharedMessageId ? { sharedMessageId: m.sharedMessageId } : {}),
          ...(m.timestamp ? { timestamp: m.timestamp } : {}),
          ...(m.isUser !== undefined ? { isUser: m.isUser } : {}),
        });
      }
    } else {
      // Only stopwords / very short — fall back to a verbatim window.
      for (const m of messages) {
        const text = typeof m?.text === 'string' ? m.text : '';
        if (!m?.threadId || !foldSearchText(text).includes(foldedQuery)) continue;
        messageHits.push({
          kind: 'message',
          threadId: m.threadId,
          title: titleById.get(m.threadId) ?? '',
          snippet: extractSnippet(text, [foldedQuery], maxSnippetChars),
          score: 0.5,
          messageId: m.id,
          ...(m.sharedMessageId ? { sharedMessageId: m.sharedMessageId } : {}),
          ...(m.timestamp ? { timestamp: m.timestamp } : {}),
          ...(m.isUser !== undefined ? { isUser: m.isUser } : {}),
        });
      }
    }
    messageHits.sort((a, b) => b.score - a.score || (b.timestamp || '').localeCompare(a.timestamp || ''));
  }

  return {
    threads: threadHits.slice(0, maxThreadHits),
    messages: messageHits.slice(0, maxMessageHits),
  };
}
